import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { BASE_URL } from '../../services/api';

const VisionMission = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${BASE_URL}/public/about.php?action=getAll`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setAboutData(result.data);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Error loading information: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ height: '140px' }}></div>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading vision and mission...</p>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ height: '140px' }}></div>
        <Container className="py-5">
          <Alert variant="danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <div className="mt-2">
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={fetchAboutData}
              >
                <i className="fas fa-redo me-1"></i>
                Try Again
              </button>
            </div>
          </Alert>
        </Container>
      </>
    );
  }

  if (!aboutData) {
    return (
      <>
        <div style={{ height: '140px' }}></div>
        <Container className="py-5">
          <Alert variant="warning">
            <i className="fas fa-info-circle me-2"></i>
            No data available. Please try again later.
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      {/* Navbar Spacer */}
      <div style={{ height: '140px' }}></div>
      
      <Container className="py-5">
        
        {/* Page Title */}
        <div className="text-center mb-5">
          <h1 className="display-4 mb-3 fw-bold text-primary">
            Vision & Mission
          </h1>
          <p className="lead text-muted max-w-800 mx-auto">
            Our guiding principles and purpose
          </p>
        </div>
        
        {/* Vision & Mission Cards */}
        <Row className="mb-5">
          <Col md={6} className="mb-4">
            <Card className="h-100 shadow-lg border-0 mission-vision-card">
              <Card.Body className="p-4">
                <div className="text-center mb-3">
                  <div className="vision-icon bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <i className="fas fa-eye fa-2x"></i>
                  </div>
                </div>
                <Card.Title className="text-center text-primary h4 mb-3">
                  Our Vision
                </Card.Title>
                <Card.Text className="text-center fs-5">
                  {aboutData.vision || 'To be a globally recognized institution of excellence in education, research, and innovation.'}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6} className="mb-4">
            <Card className="h-100 shadow-lg border-0 mission-vision-card">
              <Card.Body className="p-4">
                <div className="text-center mb-3">
                  <div className="mission-icon bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center" 
                       style={{ width: '60px', height: '60px' }}>
                    <i className="fas fa-bullseye fa-2x"></i>
                  </div>
                </div>
                <Card.Title className="text-center text-success h4 mb-3">
                  Our Mission
                </Card.Title>
                <Card.Text className="text-center fs-5">
                  {aboutData.mission || 'To provide quality education through innovative teaching-learning methods.'}
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Additional Info if needed */}
        {(aboutData.history || aboutData.description) && (
          <Row className="mt-5">
            <Col>
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <h3 className="text-center mb-4 text-secondary">
                    <i className="fas fa-info-circle me-2"></i>
                    Additional Information
                  </h3>
                  {aboutData.description && (
                    <div className="mb-4">
                      <h5 className="text-primary">About Us</h5>
                      <p>{aboutData.description}</p>
                    </div>
                  )}
                  {aboutData.history && (
                    <div>
                      <h5 className="text-primary">Our History</h5>
                      <p>{aboutData.history}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      <style>{`
        .max-w-800 {
          max-width: 800px;
        }
        
        .mission-vision-card {
          transition: all 0.3s ease;
          border-top: 4px solid #0d6efd !important;
          min-height: 300px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        
        .mission-vision-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.15) !important;
        }
        
        .mission-vision-card:nth-child(2) {
          border-top-color: #198754 !important;
        }
        
        @media (max-width: 768px) {
          .display-4 {
            font-size: 2.5rem;
          }
          
          .fs-5 {
            font-size: 1rem !important;
          }
          
          div[style*="height"] {
            height: 90px !important;
          }
        }
        
        @media (min-width: 992px) {
          div[style*="height"] {
            height: 50px !important;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .card, h1, h2, .lead {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </>
  );
};

export default VisionMission;